import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { escapeHtml } from '../utils/catalog-labels';
import { categoryLabel, type TemplateCategory } from '../utils/template-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface TemplateRow {
  id: string;
  name: string;
  category: TemplateCategory;
  description?: string | null;
  updatedAt: string;
  createdAt: string;
  _count?: { exercises: number; appliedRoutines: number };
}

export class AdminRoutineTemplatesPage implements View {
  private templates: TemplateRow[] = [];
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-templates-loading"><p>Cargando templates...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-templates-error">
          <p>${escapeHtml(this.errorMsg)}</p>
        </div>`;
    }

    const breadcrumbHtml = `<nav class="admin-breadcrumb">
      <a href="#/admin/students">Alumnos</a>
      <span class="admin-breadcrumb-sep">›</span>
      <span>Templates</span>
    </nav>`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          ${breadcrumbHtml}
          <h2 data-testid="admin-templates-title">Templates de rutina</h2>
          <p>Bases reutilizables. Al aplicar, se genera una copia independiente por alumno.</p>
        </div>
        <a href="#/admin/routine-template?form=create" class="btn btn-primary" data-testid="btn-new-template" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('plus', { size: 16 })} Nueva template</a>
      </div>

      <div class="card-glass admin-table-wrap" data-testid="admin-templates-table">
        ${
          this.templates.length === 0
            ? `<div class="admin-empty"><p>No hay templates. Ejecuta el seed o crea una nueva.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Split / categoría</th>
                <th>Ejercicios</th>
                <th>Actualizada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              ${this.templates.map((t) => this.renderRow(t)).join('')}
            </tbody>
          </table>
        `
        }
      </div>
    `;
  }

  afterRender(): void {
    document.querySelectorAll('[data-duplicate-template]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleDuplicate(btn as HTMLElement));
    });
  }

  private renderRow(t: TemplateRow): string {
    const count = t._count?.exercises ?? 0;
    const updated = new Date(t.updatedAt).toLocaleDateString('es-ES');

    return `
      <tr data-testid="template-row-${t.id}">
        <td>
          <span class="admin-cell-primary">${escapeHtml(t.name)}</span>
          ${t.description ? `<div class="admin-cell-muted">${escapeHtml(t.description.slice(0, 50))}…</div>` : ''}
        </td>
        <td><span class="badge badge-admin">${categoryLabel(t.category)}</span></td>
        <td>${count} / 10</td>
        <td>${updated}</td>
        <td class="admin-row-actions">
          <a href="#/admin/routine-template?id=${t.id}" class="btn btn-outline btn-sm" data-testid="template-view-${t.id}">Ver</a>
          <a href="#/admin/routine-template?form=edit&id=${t.id}" class="btn btn-outline btn-sm">Editar</a>
          <button type="button" class="btn btn-outline btn-sm" data-duplicate-template="${t.id}" data-testid="template-duplicate-${t.id}">Duplicar</button>
          <a href="#/admin/routine-template?form=apply&id=${t.id}" class="btn btn-primary btn-sm" data-testid="template-apply-${t.id}">Aplicar</a>
        </td>
      </tr>
    `;
  }

  private async loadData(): Promise<void> {
    try {
      this.templates = await apiClient.get<TemplateRow[]>('/api/routine-templates');
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar templates';
    } finally {
      this.loading = false;
    }
  }

  private async handleDuplicate(el: HTMLElement): Promise<void> {
    const id = el.getAttribute('data-duplicate-template');
    if (!id) return;
    try {
      await apiClient.post(`/api/routine-templates/${id}/duplicate`, {});
      toast.success('Template duplicada.');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo duplicar.');
    }
  }
}
